/* slideshow-new.js
   Version: 2026-09-08 16:12 (UTC+8) - Plaud Sharon — DTC-1228: shopify:section:load re-boot listener registered on the storefront too (not only Shopify.designMode) and matched by sectionId OR DOM containment, so the scheduled-display boundary swap (global.js armNextBannerTimers replaces #SID and dispatches the event without detail) rebinds text nav / ring / video sync; the re-boot also detaches the previous parallax scroll handler (no listener leak on the detached node)
   Version-note: 2026-08-20 20:05 (UTC+8) - Plaud Tayler — i18n: play/pause aria-labels read from data-label-* (locale keys) instead of JS literals
   ───────────────────────────────────────────────────────────────────────────
   Per-section runtime for `sections/slideshow-new.liquid` — text nav bar,
   ring countdown, center-mode teleport, parallax, video pause/play sync,
   and prefers-reduced-motion handling.

   Loaded once per page (Shopify dedupes <link> / <script> by URL). Each
   slideshow-new section calls PlaudSlideshowNew.init(SID, opts) with its
   own ID + flags via a tiny inline bootstrap.

   Queue pattern: an inline shim (`window.PlaudSlideshowNewQueue.push([...])`)
   may run BEFORE this script loads (defer + body parsing). The queue is an
   array until this file executes, then we drain it and replace `push` with
   an immediate-invoker so subsequent calls work either before or after load.
   ─────────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  function instance(SID, opts) {
    var HAS_NAV       = !!opts.hasNav;
    var HAS_CENTER    = !!opts.hasCenter;
    var HAS_PARALLAX  = !!opts.hasParallax;
    var HAS_VIDEO     = !!opts.hasVideo;
    var REDUCED_MOTION = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    var AUTOPLAY_MS   = opts.autoplayMs;
    var RING_CIRC     = 87.96;  /* 2π × r14 */
    var hasAuto       = !!opts.hasAuto;
    var retries       = 0;
    var swiper        = null;
    var btns          = [];
    var playBtn       = null;
    var ringFill      = null;
    var nav           = null;
    var wasPaused     = false;
    var barProgress   = 0;
    var ringProgress  = 0;
    var pendingRafId  = null;
    var CM_CLONES     = 0;
    var CM_REAL       = 0;
    var _teleporting  = false;
    var parallaxHandler = null;  /* DTC-1228: kept so the section:load re-boot can detach it */

    /* ══ HELPERS ══════════════════════════════════════════════════════ */
    function getRealIdx() {
      if (!swiper) return 0;
      if (HAS_CENTER && CM_REAL > 0) {
        return (swiper.activeIndex - CM_CLONES + CM_REAL) % CM_REAL;
      }
      return typeof swiper.realIndex === 'number' ? swiper.realIndex : swiper.activeIndex;
    }

    function getActiveVideo() {
      if (!swiper || !swiper.slides) return null;
      var slide = swiper.slides[swiper.activeIndex];
      return slide ? slide.querySelector('video') : null;
    }

    function syncVideoPlayback() {
      if (!swiper || !swiper.slides) return;
      var active = swiper.activeIndex;
      for (var i = 0; i < swiper.slides.length; i++) {
        var v = swiper.slides[i].querySelector('video');
        if (!v) continue;
        if (i === active) {
          if (v.paused) v.play().catch(function () {});
        } else if (!v.paused) {
          v.pause();
        }
      }
    }

    function setupVideoPauseListeners() {
      if (!swiper || !swiper.slides) return;
      for (var i = 0; i < swiper.slides.length; i++) {
        var v = swiper.slides[i].querySelector('video');
        if (!v) continue;
        v.addEventListener('play', function (e) {
          var slide = e.target.closest('.swiper-slide');
          if (slide && !slide.classList.contains('swiper-slide-active')) {
            e.target.pause();
          }
        });
      }
    }

    function resetBars() {
      if (pendingRafId !== null) {
        cancelAnimationFrame(pendingRafId);
        pendingRafId = null;
      }
      btns.forEach(function (b) {
        b.classList.remove('is-active', 'is-animating');
        var bar = b.querySelector('.sn-nav__bar');
        if (!bar) return;
        bar.style.transition = 'none';
        bar.style.width      = '0%';
        void bar.offsetWidth;
      });
    }

    function freezeBar() {
      var btn = btns[getRealIdx()];
      if (!btn) return;
      btn.classList.remove('is-animating');
      var bar = btn.querySelector('.sn-nav__bar');
      if (!bar) return;
      var pct = btn.offsetWidth > 0 ? bar.offsetWidth / btn.offsetWidth : 0;
      barProgress = pct;
      bar.style.transition = 'none';
      bar.style.width      = (pct * 100).toFixed(2) + '%';
      void bar.offsetWidth;
    }

    function resumeBar() {
      var btn = btns[getRealIdx()];
      if (!btn) return;
      var bar = btn.querySelector('.sn-nav__bar');
      if (!bar) return;
      var remaining = AUTOPLAY_MS * (1 - barProgress);
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          bar.style.transition = 'width ' + (remaining / 1000).toFixed(3) + 's linear';
          bar.style.width      = '100%';
        });
      });
    }

    function freezeRing() {
      if (!ringFill) return;
      var cur = parseFloat(window.getComputedStyle(ringFill).strokeDashoffset);
      ringProgress = 1 - (cur / RING_CIRC);
      ringFill.style.transition       = 'none';
      ringFill.style.strokeDashoffset = String(cur);
      void ringFill.offsetWidth;
      if (playBtn) playBtn.classList.remove('ring-run', 'ring-pause');
    }

    function startRing() {
      if (!ringFill || !playBtn) return;
      playBtn.classList.remove('ring-run', 'ring-pause');
      ringFill.style.transition       = 'none';
      ringFill.style.strokeDashoffset = String(RING_CIRC);
      void ringFill.offsetWidth;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          /* Clear BOTH inline overrides so the .ring-run CSS rule
             (transition: Xs linear; stroke-dashoffset: 0) can take effect.
             Without clearing inline strokeDashoffset, it stays at RING_CIRC
             and blocks the animation since inline > class specificity. */
          ringFill.style.transition       = '';
          ringFill.style.strokeDashoffset = '';
          playBtn.classList.add('ring-run');
        });
      });
    }

    function resumeRing() {
      if (!ringFill || !playBtn) return;
      playBtn.classList.remove('ring-run', 'ring-pause');
      var remaining = AUTOPLAY_MS * (1 - ringProgress);
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          ringFill.style.transition       = 'stroke-dashoffset ' + (remaining / 1000).toFixed(3) + 's linear';
          ringFill.style.strokeDashoffset = '0';
        });
      });
    }

    function setActive(idx) {
      wasPaused = false;
      resetBars();
      var btn = btns[idx];
      if (!btn) return;
      btn.classList.add('is-active');
      if (nav && nav.scrollWidth > nav.clientWidth) {
        btn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
      if (hasAuto && swiper && swiper.autoplay && swiper.autoplay.running) {
        pendingRafId = requestAnimationFrame(function () {
          pendingRafId = null;
          var bar = btn.querySelector('.sn-nav__bar');
          if (bar) {
            bar.style.transition = '';
            bar.style.width      = '';
          }
          btn.classList.add('is-animating');
        });
        startRing();
      }
      btns.forEach(function (b) {
        b.setAttribute('aria-selected', b === btn ? 'true' : 'false');
      });
    }

    /* Labels come from Liquid data-* (locale keys): a JS literal cannot be translated and
       stayed English on every localized storefront. Fallbacks keep old behaviour if the
       attribute is absent - Plaud Tayler - 2026-08-20 20:05 */
    function labelPause() {
      return (playBtn && playBtn.getAttribute('data-label-pause')) || 'Pause slideshow';
    }
    function labelPlay() {
      return (playBtn && playBtn.getAttribute('data-label-play')) || 'Play slideshow';
    }

    function syncPlayIcon(running) {
      if (!playBtn) return;
      var pauseEl = playBtn.querySelector('.sn-icon--pause');
      var playEl  = playBtn.querySelector('.sn-icon--play');
      if (running) {
        if (pauseEl) pauseEl.style.display = '';
        if (playEl)  playEl.style.display  = 'none';
        playBtn.setAttribute('aria-label', labelPause());
        playBtn.setAttribute('aria-pressed', 'true');
      } else {
        if (pauseEl) pauseEl.style.display = 'none';
        if (playEl)  playEl.style.display  = '';
        playBtn.setAttribute('aria-label', labelPlay());
        playBtn.setAttribute('aria-pressed', 'false');
      }
    }

    /* ══ NAV INIT ═════════════════════════════════════════════════════ */
    function initSwiper() {
      nav      = HAS_NAV ? document.querySelector('.sn-nav-' + SID) : null;
      var swEl = document.getElementById('swiper-' + SID);
      playBtn  = document.querySelector('.sn-play-pause-' + SID);
      ringFill = playBtn ? playBtn.querySelector('.sn-ring__fill') : null;

      if (HAS_NAV && !nav) return;
      if (!swEl) return;

      swiper = swEl.globalSlide;
      if (!swiper) {
        if (++retries < 40) setTimeout(initSwiper, 200);
        return;
      }

      /* C2: Respect prefers-reduced-motion — silence autoplay before any UI sync. */
      if (REDUCED_MOTION && swiper.autoplay && swiper.autoplay.running) {
        swiper.autoplay.stop();
        hasAuto = false;
      }

      if (HAS_NAV) {
        btns = Array.prototype.slice.call(nav.querySelectorAll('.sn-nav__btn'));
      }

      if (HAS_CENTER) {
        CM_CLONES = parseInt(swEl.dataset.centerClones, 10) || 2;
        CM_REAL   = swiper.slides.length - 2 * CM_CLONES;
      }

      swiper.on('slideChange', function () {
        setActive(getRealIdx());
        if (HAS_VIDEO) syncVideoPlayback();
      });

      if (HAS_CENTER) {
        swiper.on('slideChangeTransitionEnd', function () {
          if (_teleporting) return;
          var idx = swiper.activeIndex;
          if (idx < CM_CLONES) {
            _teleporting = true;
            swiper.slideTo(idx + CM_REAL, 0, false);
            _teleporting = false;
          } else if (idx >= CM_CLONES + CM_REAL) {
            _teleporting = true;
            swiper.slideTo(idx - CM_REAL, 0, false);
            _teleporting = false;
          }
        });
      }

      swiper.on('autoplayStart', function () {
        hasAuto = true;
        syncPlayIcon(true);
        var vid = getActiveVideo();
        if (vid && vid.paused) vid.play().catch(function () {});
        if (wasPaused) {
          resumeBar();
          resumeRing();
          wasPaused = false;
        } else {
          setActive(getRealIdx());
        }
      });

      swiper.on('autoplayStop', function () {
        hasAuto   = false;
        wasPaused = true;
        syncPlayIcon(false);
        freezeBar();
        freezeRing();
        var vid = getActiveVideo();
        if (vid) vid.pause();
      });

      swiper.on('autoplayPause', function () {
        if (!wasPaused) { freezeBar(); freezeRing(); }
      });
      swiper.on('autoplayResume', function () {
        if (!wasPaused) { resumeBar(); resumeRing(); }
      });

      btns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var target = parseInt(btn.getAttribute('data-sn-index'), 10);
          if (HAS_CENTER && CM_REAL > 0) {
            swiper.slideTo(target + CM_CLONES, 600);
          } else {
            swiper.slideToLoop(target, 600);
          }
        });
      });

      if (playBtn) {
        playBtn.addEventListener('click', function () {
          if (swiper.autoplay && swiper.autoplay.running) {
            swiper.autoplay.stop();
          } else {
            hasAuto = true;
            swiper.autoplay.start();
          }
        });
      }

      var running = !!(swiper.autoplay && swiper.autoplay.running);
      setActive(getRealIdx());
      syncPlayIcon(running);
      if (!running) freezeRing();
      if (HAS_VIDEO) {
        setupVideoPauseListeners();
        syncVideoPlayback();
      }
    }

    /* ══ PARALLAX ═════════════════════════════════════════════════════ */
    function initParallax() {
      if (!HAS_PARALLAX) return;
      var sec = document.getElementById(SID);
      if (!sec) return;
      var ticking = false;
      function update() {
        var rect = sec.getBoundingClientRect();
        var secH = sec.offsetHeight;
        if (rect.bottom < 0 || rect.top > window.innerHeight) { ticking = false; return; }
        var progress = -rect.top / secH;
        var offset   = (progress * secH * 0.12);
        sec.style.setProperty('--sn-py', offset.toFixed(1) + 'px');
        ticking = false;
      }
      parallaxHandler = function () {
        if (!ticking) { requestAnimationFrame(update); ticking = true; }
      };
      window.addEventListener('scroll', parallaxHandler, { passive: true });
      update();
    }

    /* ══ BOOT ═════════════════════════════════════════════════════════ */
    function boot() {
      if (HAS_NAV || HAS_CENTER || HAS_VIDEO || REDUCED_MOTION) initSwiper();
      if (HAS_PARALLAX && !REDUCED_MOTION && window.matchMedia('(min-width:992px)').matches) initParallax();
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }

    /* Re-boot when this section's DOM is replaced. Two emitters: the theme editor (detail.sectionId
       set) and the storefront scheduled-display boundary refetch in global.js armNextBannerTimers,
       which swaps #SID and dispatches shopify:section:load on the wrapper WITHOUT detail — so match
       by DOM containment as a fallback. Node.contains is inclusive, so a dispatch on the root itself
       also matches. Other sections' events never contain this root -> ignored. - DTC-1228 */
    document.addEventListener('shopify:section:load', function (e) {
      var byId  = !!(e.detail && e.detail.sectionId === SID);
      var root  = document.getElementById(SID);
      var byDom = !byId && !!root && !!e.target && typeof e.target.contains === 'function' && e.target.contains(root);
      if (!byId && !byDom) return;
      if (pendingRafId !== null) { cancelAnimationFrame(pendingRafId); }
      retries      = 0;
      swiper       = null;
      btns         = [];
      playBtn      = null;
      ringFill     = null;
      nav          = null;
      wasPaused    = false;
      barProgress  = 0;
      ringProgress = 0;
      pendingRafId = null;
      /* detach the previous parallax scroll handler: it closes over the detached old #SID node */
      if (parallaxHandler) { window.removeEventListener('scroll', parallaxHandler); parallaxHandler = null; }
      setTimeout(boot, 300);
    });
  }

  /* Drain any inline-shim queued calls, then replace the queue with an
     immediate-invoker so future pushes call instance() directly. */
  var existing = window.PlaudSlideshowNewQueue;
  var pending = (existing && Array.isArray(existing._items || existing)) ? (existing._items || existing) : [];
  window.PlaudSlideshowNewQueue = {
    push: function (args) { instance.apply(null, args); }
  };
  pending.forEach(function (args) { instance.apply(null, args); });

  /* Public API for callers that prefer an explicit init. */
  window.PlaudSlideshowNew = { init: function (SID, opts) { instance(SID, opts); } };
})();
